import { Badge } from '@ipfabric/ui-components/Badge/Badge';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { Tabs } from '@ipfabric/ui-components/Tabs/Tabs';
import { useMemo, useState } from 'react';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import type { RuleTargetType } from '../ruleTypes';
import {
  conditionsToFilter,
  conditionsToUnmatchedFilter,
} from './ruleFilterMapping';
import {
  RULE_TEST_COLUMNS,
  type RuleFormValues,
  testColumnLabel,
} from './ruleFormSchema';
import { useTestRuleQueries } from './useTestRuleQueries';

type TestRuleTab = 'matched' | 'unmatched';

type TestRuleViewProps = {
  conditions: RuleFormValues['conditions'];
  type: RuleTargetType;
};

const readRowString = (row: unknown, column: string): string | undefined => {
  if (row === null || typeof row !== 'object') {
    return undefined;
  }
  const value = Reflect.get(row, column);
  return typeof value === 'string' ? value : undefined;
};

export const TestRuleView = ({ conditions, type }: TestRuleViewProps) => {
  const snapshotId = useSnapshotId();
  const [activeTab, setActiveTab] = useState<TestRuleTab>('matched');
  const testColumns = RULE_TEST_COLUMNS[type];
  const emptyLabel = type === 'intL2' ? 'interfaces' : 'devices';

  const validConditions = conditions.filter((condition) => condition.regex);
  const { matchedQuery, unmatchedQuery } = useTestRuleQueries({
    type,
    snapshotId,
    matchedFilters: conditionsToFilter(validConditions),
    unmatchedFilters: conditionsToUnmatchedFilter(validConditions),
  });
  const activeQuery = activeTab === 'matched' ? matchedQuery : unmatchedQuery;

  const tabs = useMemo(
    () => [
      {
        badge: matchedQuery.isSuccess ? (
          <Badge color='neutral' variant='plain'>
            {matchedQuery.data._meta.count}
          </Badge>
        ) : undefined,
        id: 'matched' as const,
        label: 'Matched',
      },
      {
        badge: unmatchedQuery.isSuccess ? (
          <Badge color='neutral' variant='plain'>
            {unmatchedQuery.data._meta.count}
          </Badge>
        ) : undefined,
        id: 'unmatched' as const,
        label: 'Unmatched',
      },
    ],
    [
      matchedQuery.data?._meta.count,
      matchedQuery.isSuccess,
      unmatchedQuery.data?._meta.count,
      unmatchedQuery.isSuccess,
    ],
  );

  const isLoading = matchedQuery.isLoading || unmatchedQuery.isLoading;
  const isError = matchedQuery.isError || unmatchedQuery.isError;

  return (
    <div className='flex flex-col'>
      <Tabs
        tabs={tabs}
        value={activeTab}
        onChange={setActiveTab}
        variant='outer'
      />
      <Card className='flex flex-col gap-xs p-sm rounded-t-none'>
        {isLoading && <div className='text-sm text-gray-500'>Loading…</div>}
        {isError && (
          <div className='text-sm text-red-600'>
            Failed to load {emptyLabel}. Check that conditions use valid regex
            patterns.
          </div>
        )}
        {activeQuery.isSuccess && (
          <>
            {activeQuery.data.data.length > 0 && (
              <div className='flex flex-col overflow-hidden'>
                <div className='grid grid-cols-2 gap-xs text-xs font-medium text-gray-500 border-b py-xs'>
                  {testColumns.map((column) => (
                    <span key={column}>{testColumnLabel(column)}</span>
                  ))}
                </div>
                <div className='overflow-y-auto max-h-[300px]'>
                  {activeQuery.data.data.map((row, index) => (
                    <div
                      key={
                        type === 'intL2'
                          ? `${readRowString(row, 'hostname')}-${readRowString(row, 'intName')}-${index}`
                          : (readRowString(row, 'hostname') ?? String(index))
                      }
                      className='grid grid-cols-2 gap-xs text-sm border-b py-xs'
                    >
                      {testColumns.map((column) => (
                        <span key={column} className='truncate'>
                          {readRowString(row, column) ?? '—'}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {activeQuery.data._meta.count > 0 && (
              <div className='text-xs text-gray-500 pt-xs'>
                {activeQuery.data._meta.count} items total
              </div>
            )}
            {activeQuery.data._meta.count === 0 && (
              <div className='text-sm text-gray-500'>
                {activeTab === 'matched'
                  ? `No matching ${emptyLabel} found.`
                  : `No unmatched ${emptyLabel} found.`}
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};
